import React from "react";

import { IMessage, IMessagesActions, IMessagesContext, TMessageType } from "./types";

export interface IMessagesProps {}

interface State extends IMessagesContext {}

export const MessagesContext = React.createContext({} as IMessagesContext);
const MessegesProvider = MessagesContext.Provider;
const MessagesConsumer = MessagesContext.Consumer;

const DEFAULT_CONTEXT = "default";

const DEFAULTS = {
  type: "information" as TMessageType,
  context: DEFAULT_CONTEXT,
  lifeTime: 3600,
};

export class MessagesProvider extends React.Component<IMessagesProps, State> {
  // require as setTimeout returns number or Timeout depending on the platform
  timeouts: Map<IMessage["id"], ReturnType<typeof setTimeout>>;
  messages: IMessage[];

  constructor(props: IMessagesProps) {
    super(props);

    this.timeouts = new Map();
    this.messages = [];

    this.state = {
      contextMessages: {},
      addMessage: this.addMessage,
      deleteMessage: this.deleteMessage,
      deleteAllMessages: this.deleteAllMessages,
      stopMessageDying: this.clearTimeout,
      startMessageToLiveAgain: this.setTimeout,
    };
  }

  clearTimeout = (message: IMessage) => {
    const timeout = this.timeouts.get(message.id);

    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(message.id);
    }
  };

  setTimeout = (message: IMessage) => {
    this.clearTimeout(message);

    if (message.lifeTime && message.lifeTime !== Infinity) {
      const timeout = setTimeout(() => {
        this.deleteMessage(message);
      }, message.lifeTime);
      this.timeouts.set(message.id, timeout);
    }
  };

  getId = (payload: Partial<IMessage>) => {
    const { context, type, title, text } = payload;
    return [context, type, title, text].filter(v => v).join("-");
  };

  alreadyExist = (payload: IMessage) => this.messages.find(m => m.id === payload.id);

  createMessage = (payload: Partial<IMessage>): IMessage => {
    const message = { ...DEFAULTS, ...payload };
    const id = this.getId(message);

    return { ...message, id };
  };

  addMessage = (payload: Partial<IMessage>) => {
    const message = this.createMessage(payload);
    this.setTimeout(message);

    if (this.alreadyExist(message)) {
      return message;
    }

    const messages = this.state.contextMessages[message.context] || [];

    this.messages = [...this.messages, message];
    this.setState({
      contextMessages: {
        ...this.state.contextMessages,
        [message.context]: [...messages, message],
      },
    });

    return message;
  };

  deleteMessage = (payload: IMessage) => {
    const messages = this.state.contextMessages[payload.context] || [];

    this.clearTimeout(payload);
    this.messages = this.messages.filter(m => m.id !== payload.id);

    this.setState({
      contextMessages: {
        ...this.state.contextMessages,
        [payload.context]: messages.filter(m => m.id !== payload.id),
      },
    });
  };

  deleteAllMessages = (context = DEFAULTS.context) => {
    this.messages.filter(m => m.context === context).map(this.clearTimeout);
    this.messages = this.messages.filter(m => m.context !== context);

    this.setState((prevState: State) => {
      return {
        contextMessages: {
          ...prevState.contextMessages,
          [context]: [],
        },
      };
    });
  };

  render() {
    const { children } = this.props;
    return <MessegesProvider value={this.state}>{children}</MessegesProvider>;
  }
}

export interface IOutWithMessagesProps {
  context?: IMessage["context"];
}

export interface IWithMessagesProps extends IOutWithMessagesProps, IMessagesActions {
  messages: IMessage[];
}

export function withMessages<P extends IWithMessagesProps>(WrappedComponent: React.ComponentType<P>) {
  type Props = Omit<P, keyof IWithMessagesProps> & IOutWithMessagesProps;
  return class WithMessages extends React.Component<Props> {
    render() {
      const { context = DEFAULT_CONTEXT } = this.props;
      return (
        <MessagesConsumer>
          {value => (
            <WrappedComponent {...this.props} {...(value as any)} messages={value.contextMessages[context] || []} />
          )}
        </MessagesConsumer>
      );
    }
  };
}

export interface IWithMessagesActions extends IMessagesActions {}

export function withMessagesActions<P extends IMessagesActions>(WrappedComponent: React.ComponentType<P>) {
  return class WithMessagesActions extends React.Component<Omit<P, keyof IWithMessagesActions>> {
    render() {
      return (
        <MessagesConsumer>
          {value => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { contextMessages, ...actions } = value;

            return <WrappedComponent {...this.props} {...(actions as any)} />;
          }}
        </MessagesConsumer>
      );
    }
  };
}
